import type { NodePropertyValue } from '../../../types/properties';

/**
 * Validates Evo Web Feed node configuration
 */
export function validate(config: Record<string, NodePropertyValue>): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Required fields for all operations
  if (!config.subscriptionId || String(config.subscriptionId).trim() === '') {
    errors.push('Subscription ID is required');
  }

  if (!config.correlationId || String(config.correlationId).trim() === '') {
    errors.push('Correlation ID is required');
  } else {
    // Validate correlationId format - no spaces allowed
    const correlationId = String(config.correlationId);
    if (correlationId.includes(' ')) {
      errors.push('Correlation ID cannot contain spaces (use hyphens or underscores instead)');
    }
  }

  // Validate userIds collection
  const userIds = config.userIds as { users?: Array<{ userId: string }> };
  if (!userIds?.users || userIds.users.length === 0) {
    errors.push('At least one user ID is required');
  } else if (userIds.users.length > 100) {
    errors.push('Maximum 100 users allowed per feed item');
  } else {
    // Validate each user ID
    userIds.users.forEach((user, index) => {
      if (!user.userId || String(user.userId).trim() === '') {
        errors.push(`User ID #${index + 1} is empty`);
      }
    });
  }

  // Operation-specific validation
  const operation = config.operation as string;

  if (operation === 'send' || operation === 'update') {
    // Title validation
    if (!config.title || String(config.title).trim() === '') {
      errors.push('Title is required for send/update operations');
    } else if (String(config.title).length > 50) {
      errors.push('Title must be 50 characters or less');
    }

    // Description validation
    if (!config.description || String(config.description).trim() === '') {
      errors.push('Description is required for send/update operations');
    } else if (String(config.description).length > 800) {
      errors.push('Description must be 800 characters or less');
    }

    // Validate date if included
    if (config.includeDate) {
      if (!config.date || String(config.date).trim() === '') {
        errors.push('Date is required when "Include Date" is enabled');
      }
    }

    // Validate links if included
    if (config.includeLinks) {
      const links = config.links as { 
        items?: Array<{ 
          text: string; 
          desktopUrl?: string; 
          widgetId?: string;
          includeMobileUrl?: boolean;
          mobileUrl?: string;
        }> 
      };
      
      if (!links?.items || links.items.length === 0) {
        errors.push('At least one link is required when "Include Links" is enabled');
      } else {
        links.items.forEach((link, index) => {
          // Text is required
          if (!link.text || String(link.text).trim() === '') {
            errors.push(`Link #${index + 1}: Text is required`);
          } else if (String(link.text).length > 50) {
            errors.push(`Link #${index + 1}: Text must be 50 characters or less`);
          }
          
          // At least one of desktopUrl or widgetId must be provided
          const hasDesktopUrl = link.desktopUrl && String(link.desktopUrl).trim() !== '';
          const hasWidgetId = link.widgetId && String(link.widgetId).trim() !== '';
          
          if (!hasDesktopUrl && !hasWidgetId) {
            errors.push(`Link #${index + 1}: Either Desktop URL or Widget ID must be provided`);
          }
          
          // Validate mobile URL if toggle is enabled
          if (link.includeMobileUrl) {
            if (!link.mobileUrl || String(link.mobileUrl).trim() === '') {
              errors.push(`Link #${index + 1}: Mobile URL is required when "Different Mobile URL" is enabled`);
            }
          }
        });
      }
    }

    // Validate prompts if included (max 3)
    if (config.includePrompts) {
      const prompts = config.prompts as { items?: Array<{ text: string }> };
      if (!prompts?.items || prompts.items.length === 0) {
        errors.push('At least one prompt is required when "Include Copilot Prompts" is enabled');
      } else if (prompts.items.length > 3) {
        errors.push('Maximum 3 prompts allowed');
      } else {
        prompts.items.forEach((prompt, index) => {
          if (!prompt.text || String(prompt.text).trim() === '') {
            errors.push(`Prompt #${index + 1} text is required`);
          } else if (String(prompt.text).length > 200) {
            errors.push(`Prompt #${index + 1} text must be 200 characters or less`);
          }
        });
      }
    }

    // Validate actions if included (max 3)
    if (config.includeActions) {
      const actions = config.actions as {
        items?: Array<{
          actionId: string;
          backendId: string;
          text: string;
          uri: string;
        }>;
      };
      if (!actions?.items || actions.items.length === 0) {
        errors.push('At least one action is required when "Include Actions" is enabled');
      } else if (actions.items.length > 3) {
        errors.push('Maximum 3 actions allowed');
      } else {
        actions.items.forEach((action, index) => {
          if (!action.actionId || String(action.actionId).trim() === '') {
            errors.push(`Action #${index + 1} ID is required`);
          }
          if (!action.backendId || String(action.backendId).trim() === '') {
            errors.push(`Action #${index + 1} backend ID is required`);
          }
          if (!action.text || String(action.text).trim() === '') {
            errors.push(`Action #${index + 1} button text is required`);
          }
          if (!action.uri || String(action.uri).trim() === '') {
            errors.push(`Action #${index + 1} URI is required`);
          }
        });
      }
    }

    // Validate MFE if included
    if (config.includeMfe) {
      if (!config.mfeId || String(config.mfeId).trim() === '') {
        errors.push('MFE ID is required when "Include MFE" is enabled');
      } else if (String(config.mfeId).length > 250) {
        errors.push('MFE ID must be 250 characters or less');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export default validate;
